import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Button, Group, Modal, Text, TextInput } from '@mantine/core';
import { AxiosError } from 'axios';
import { useState } from 'react';

import { useSetManagerTelegram } from '../api/setManagerTelegram';
import { managerTypes } from '../types';

import { Departments } from '@/types';

type Params = {
  manager: managerTypes.Manager;
  department: Departments;
};

export const TelegramModal = NiceModal.create(({ manager, department }: Params) => {
  const modal = useModal();

  const [id, setId] = useState(manager.telegram?.id ? String(manager.telegram.id) : '');
  const [username, setUsername] = useState(manager.telegram?.username || '');

  const { mutate, isLoading, variables, error } = useSetManagerTelegram({
    config: { onSuccess: modal.remove },
    department,
  });

  const submitError = error
    ? (error as AxiosError<{ error?: string }>).response?.data?.error ||
      'Could not save the telegram account'
    : undefined;

  const isIdValid = /^\d+$/.test(id.trim()) && Number(id.trim()) > 0;
  const isUnlinking = isLoading && !variables?.telegram;

  const submit = () => {
    if (!isIdValid) {
      return;
    }
    mutate({
      managerId: manager._id,
      telegram: { id: Number(id.trim()), username: username.trim() || undefined },
    });
  };

  const unlink = () => mutate({ managerId: manager._id });

  return (
    <Modal
      centered
      overlayProps={{ opacity: 0.4, blur: 1.5 }}
      opened={modal.visible}
      onClose={() => modal.remove()}
      title={`${manager.username} telegram`}
    >
      <Text my="xs" size="sm" c="dimmed">
        Needed so checks recorded in the mobile CRM can be sent to the bot for review.
      </Text>
      <Text my="xs" size="sm">
        Telegram user id
      </Text>
      <TextInput
        autoComplete="off"
        placeholder="123456789"
        value={id}
        onChange={(event) => setId(event.currentTarget.value)}
        error={id && !isIdValid ? 'Must be a positive number' : undefined}
      />
      <Text my="xs" size="sm">
        Username
      </Text>
      <TextInput
        autoComplete="off"
        placeholder="username"
        value={username}
        onChange={(event) => setUsername(event.currentTarget.value)}
        mb={submitError ? 'xs' : 'xl'}
      />
      {submitError && (
        <Text mb="xl" size="sm" c="red">
          {submitError}
        </Text>
      )}
      <Group justify="right">
        {manager.telegram && (
          <Button
            variant="subtle"
            color="red"
            loading={isUnlinking}
            disabled={isLoading && !isUnlinking}
            onClick={unlink}
          >
            Unlink
          </Button>
        )}
        <Button
          loading={isLoading && !isUnlinking}
          disabled={!isIdValid || isUnlinking}
          onClick={submit}
        >
          Save
        </Button>
      </Group>
    </Modal>
  );
});
